import { useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { capitaliseFirstLetter } from "@shared/translations/src/format";
import ky from "ky";
import { useRef, useState } from "react";
import { type Control, useForm, type UseFormReturn } from "react-hook-form";
import { NumericFormat, PatternFormat } from "react-number-format";
import { useClickAway, useDebounce } from "react-use";
import { z } from "zod";
import {
  type LoqateFindAddress,
  loqateFindAddressSchema,
  loqateRetrieveAddressSchema,
  contactSchema,
  SUPPLY_CHAIN_PARTNER_ROLES,
  type SupplyChainPartnerRole,
} from "./validation";
import { getStateOptions } from "@/benchmarks/BenchmarkFilters/SourceStatePicker";
import { Combobox } from "@/components/Combobox";
import { type ComboboxOption } from "@/components/Combobox/types";
import { FormItemWithMessage } from "@/components/FormItemWithMessage";
import { SelectInput } from "@/components/SelectInput";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from "@/components/ui/command";
import { Form, FormField } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { graphql, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useToast } from "@/hooks/use-toast";
import { removeNothings } from "@/lib/utils/arrays";
import { cn } from "@/lib/utils/common";
import { getErrorMessage } from "@/lib/utils/errors";
import { formatEnumLabel, formatName } from "@/lib/utils/formatting";
import { isValidStateCode, STATE_DATA } from "@/lib/utils/states";

const LOQATE_FIND_URL = "https://api.addressy.com/Capture/Interactive/Find/v1.1/json3.ws";
const LOQATE_RETRIEVE_URL = "https://api.addressy.com/Capture/Interactive/Retrieve/v1.2/json3.ws";
const ADDRESS_LIMIT = 6;

type BaseContactFormProps = {
  onSubmit: (values: z.infer<typeof contactSchema>) => Promise<void>;
  onCancel?: () => void;
  initialData?: z.infer<typeof contactSchema>;
};

type ContactFormWithoutDelete = BaseContactFormProps & {
  onDelete?: undefined;
  isDeleting?: undefined;
};

type ContactFormWithDelete = BaseContactFormProps & {
  onDelete: () => void;
  isDeleting: boolean;
};

type ContactFormProps = ContactFormWithoutDelete | ContactFormWithDelete;

export function ContactForm({ onSubmit, onCancel, initialData, onDelete, isDeleting }: ContactFormProps) {
  const [showAdditionalAddressFields, setShowAdditionalAddressFields] = useState(Boolean(initialData));

  const { activeBusinessUnitIdInt } = useActiveBusinessUnit();

  const {
    data: usersData,
    loading: userDataLoading,
    error: userDataError,
  } = useQuery(businessUnitUsersQuery, {
    variables: { businessUnitId: activeBusinessUnitIdInt },
  });

  const userOptions: ComboboxOption[] = removeNothings(usersData?.businessUnitUsers || []).map((businessUnitUser) => {
    return {
      label: formatName({ firstName: businessUnitUser.user.firstName, lastName: businessUnitUser.user.lastName }),
      value: businessUnitUser.user.id,
    };
  });

  const form = useForm<z.infer<typeof contactSchema>>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      businessName: initialData?.businessName || "",
      accountNumber: initialData?.accountNumber || "",
      contactFirstName: initialData?.contactFirstName || "",
      contactLastName: initialData?.contactLastName || "",
      contactEmail: initialData?.contactEmail || "",
      contactPhone: initialData?.contactPhone || "",
      businessAccountManagerId: initialData?.businessAccountManagerId || "",
      type: initialData?.type || "",
      role: initialData?.role || undefined,
      addressLine1: initialData?.addressLine1 || "",
      addressLine2: initialData?.addressLine2 || "",
      city: initialData?.city || "",
      state: initialData?.state || undefined,
      postCode: initialData?.postCode || "",
      countryId: "12", // US as default country ID.
      monthlyTarget: initialData?.monthlyTarget || undefined,
      note: initialData?.note || "",
    },
  });

  const addressValidationError = Boolean(
    form.formState.errors.addressLine1 ||
      form.formState.errors.city ||
      form.formState.errors.state ||
      form.formState.errors.postCode,
  );

  const roleOptions = getRoleOptions(SUPPLY_CHAIN_PARTNER_ROLES);
  const {
    data: supplyChainPartnerTypeData,
    loading: supplyChainPartnerTypeDataLoading,
    error: supplyChainPartnerTypeDataError,
  } = useQuery(supplyChainPartnerTypesQuery);

  // Filter types based on selected role
  const allTypes = removeNothings(supplyChainPartnerTypeData?.supplyChainPartnerTypes?.edges || []);
  const filteredTypes = form.watch("role") ? allTypes.filter((edge) => edge?.node?.role === form.watch("role")) : [];

  const supplyChainPartnerTypeOptions = getTypeOptions(filteredTypes);

  const handleCancel = () => {
    form.reset();
    setShowAdditionalAddressFields(false);

    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-3 gap-x-3">
        <FormField
          control={form.control}
          name="businessName"
          render={({ field }) => (
            <FormItemWithMessage
              label="Business Name *"
              inputComponent={<Input placeholder="Enter business name" {...field} />}
            />
          )}
        />
        <FormField
          control={form.control}
          name="accountNumber"
          render={({ field }) => (
            <FormItemWithMessage
              label="Account Number"
              inputComponent={<Input placeholder="Enter account number" {...field} />}
            />
          )}
        />
        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItemWithMessage
              label="Role *"
              inputComponent={
                <SelectInput
                  {...field}
                  placeholder="Select role"
                  options={roleOptions}
                  value={field.value || ""}
                  onChange={(value) => {
                    field.onChange(value);
                    if (value) {
                      // Filter types by the selected role
                      const matchingTypes = allTypes.filter((edge) => edge?.node?.role === value);
                      const typeOptions = getTypeOptions(matchingTypes);
                      // Set type to first option if available, otherwise empty string
                      form.setValue("type", (typeOptions.length > 0 ? typeOptions[0]?.value : "") || "");
                    } else {
                      form.setValue("type", "");
                    }
                  }}
                />
              }
            />
          )}
        />
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItemWithMessage
              label="Operation Type *"
              inputComponent={
                <SelectInput
                  {...field}
                  loading={supplyChainPartnerTypeDataLoading}
                  placeholder={
                    supplyChainPartnerTypeOptions.length === 0 ? "Please select a role first" : "Select type"
                  }
                  options={supplyChainPartnerTypeOptions}
                  disabled={
                    supplyChainPartnerTypeOptions.length === 0 ||
                    supplyChainPartnerTypeDataLoading ||
                    Boolean(supplyChainPartnerTypeDataError)
                  }
                  error={supplyChainPartnerTypeDataError}
                  value={field.value || ""}
                  onChange={(value) => {
                    // Ignore empty values from Radix UI's onValueChange initialization
                    // See: https://github.com/radix-ui/primitives/issues/3135
                    if (value) field.onChange(value);
                  }}
                />
              }
            />
          )}
        />
        <FormField
          control={form.control}
          name="monthlyTarget"
          render={({ field }) => (
            <FormItemWithMessage
              label="Monthly Target"
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  allowNegative={false}
                  value={field.value}
                  onValueChange={(values) => {
                    field.onChange(values.value);
                  }}
                  thousandSeparator=","
                  decimalSeparator="."
                  maxLength={7}
                  decimalScale={0}
                  placeholder="Enter monthly target"
                />
              }
            />
          )}
        />
        <fieldset className="md:col-span-3 border-0 p-0 m-0">
          <legend
            className={cn(
              "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 py-2",
              addressValidationError ? "text-red-500" : "",
            )}
          >
            Address *
          </legend>
          {showAdditionalAddressFields ? null : (
            <AddressSearch
              hasError={addressValidationError}
              form={form}
              setShowAdditionalAddressFields={setShowAdditionalAddressFields}
            />
          )}

          <div
            className={cn(
              "grid transition-all duration-300 ease-out",
              showAdditionalAddressFields ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
            )}
          >
            <div className="overflow-hidden">
              <div className="flex flex-col">
                {showAdditionalAddressFields && <AddressFields control={form.control} />}
              </div>
            </div>
          </div>
        </fieldset>
        <fieldset className="md:col-span-3 border-0 p-0 m-0">
          <legend className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 py-2">
            Principal Contact
          </legend>
          <div className="grid md:grid-cols-3 gap-x-3">
            <div className="grid grid-cols-2 gap-x-3 md:col-span-2">
              <FormField
                control={form.control}
                name="contactFirstName"
                render={({ field }) => (
                  <FormItemWithMessage
                    label="First Name"
                    inputComponent={<Input placeholder="Enter first name" {...field} />}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="contactLastName"
                render={({ field }) => (
                  <FormItemWithMessage
                    label="Last Name"
                    inputComponent={<Input placeholder="Enter last name" {...field} />}
                  />
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="contactPhone"
              render={({ field }) => (
                <FormItemWithMessage
                  label="Phone Number"
                  inputComponent={
                    <PatternFormat
                      customInput={Input}
                      getInputRef={field.ref}
                      format="(###) ###-####"
                      mask="_"
                      allowEmptyFormatting={false}
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={field.value}
                      onValueChange={(values) => {
                        field.onChange(values.value);
                      }}
                    />
                  }
                />
              )}
            />
            <FormField
              control={form.control}
              name="contactEmail"
              render={({ field }) => (
                <FormItemWithMessage
                  label="Email"
                  inputComponent={<Input type="email" placeholder="info@contact.com" {...field} />}
                />
              )}
            />
          </div>
        </fieldset>
        <FormField
          control={form.control}
          name="businessAccountManagerId"
          render={({ field }) => (
            <FormItemWithMessage
              label="Business Account Manager"
              inputComponent={
                <Combobox
                  type="single"
                  loading={userDataLoading}
                  error={userDataError}
                  placeholder="Select Manager"
                  options={userOptions}
                  selected={field.value}
                  onChange={(value) => form.setValue("businessAccountManagerId", value || "")}
                />
              }
            />
          )}
        />
        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItemWithMessage
              label="Note"
              className="md:col-span-3"
              inputComponent={<Textarea placeholder="Enter note" className="resize-none" {...field} />}
            />
          )}
        />
        <div
          className={cn(
            "md:col-span-full mt-5 flex flex-col md:flex-row ",
            Boolean(onDelete) ? "md:justify-between" : "md:justify-end",
          )}
        >
          {onDelete ? (
            <Button
              disabled={form.formState.isSubmitting || isDeleting}
              type="button"
              variant="destructive"
              onClick={onDelete}
              className="w-full md:w-auto"
              isLoading={isDeleting}
            >
              Delete
            </Button>
          ) : null}
          <div className="md:flex md:gap-2">
            <Button
              disabled={form.formState.isSubmitting || isDeleting}
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="w-full md:w-auto my-2 md:my-0"
            >
              Cancel
            </Button>
            <Button
              isLoading={form.formState.isSubmitting}
              disabled={form.formState.isSubmitting || isDeleting}
              type="submit"
              variant="confirm"
              className="w-full md:w-auto"
            >
              {initialData ? "Save Changes" : "Save and Create"}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

function AddressSearch({
  hasError,
  form,
  setShowAdditionalAddressFields,
}: {
  hasError: boolean;
  form: UseFormReturn<z.infer<typeof contactSchema>>;
  setShowAdditionalAddressFields: (value: boolean) => void;
}) {
  const { toast } = useToast();
  const [addressList, setAddressList] = useState<LoqateFindAddress["Items"]>([]);
  const [isAddressOptionsVisible, setIsAddressOptionsVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const commandRef = useRef<HTMLDivElement>(null);

  useClickAway(commandRef, () => {
    setIsAddressOptionsVisible(false);
  });

  const handleFindAddress = async (searchTerm: string) => {
    setIsLoading(true);

    try {
      const response = await ky
        .post(LOQATE_FIND_URL, {
          searchParams: {
            key: import.meta.env.VITE_LOQATE_API_KEY,
            text: searchTerm,
            countries: "US",
            limit: ADDRESS_LIMIT,
          },
        })
        .json();

      const parsedResponse = loqateFindAddressSchema.parse(response);
      setAddressList(parsedResponse.Items.filter((addr) => addr.Type === "Address"));
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description:
          error instanceof z.ZodError
            ? "Invalid data received from address service"
            : getErrorMessage(error) || "There was a problem with your request.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddressSelect = async (id: string) => {
    try {
      const response = await ky
        .post(LOQATE_RETRIEVE_URL, {
          searchParams: {
            key: import.meta.env.VITE_LOQATE_API_KEY,
            id: id,
          },
        })
        .json();

      const parsedResponse = loqateRetrieveAddressSchema.parse(response);
      const address = parsedResponse.Items[0];

      if (!address) {
        toast({
          variant: "destructive",
          title: "Uh oh! Something went wrong.",
          description: "Error selecting address.",
        });

        return;
      }

      form.setValue("addressLine1", address.Line1);
      form.setValue("addressLine2", address.Line2 || "");
      form.setValue("city", address.City);
      form.setValue("postCode", address.PostalCode);
      form.setValue("state", isValidStateCode(address.ProvinceCode) ? address.ProvinceCode : undefined);

      setIsAddressOptionsVisible(false);
      setAddressList([]);
      setShowAdditionalAddressFields(true);
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description:
          error instanceof z.ZodError
            ? "Invalid data received from address service"
            : getErrorMessage(error) || "There was a problem with your request.",
      });
    }
  };

  useDebounce(
    () => {
      const trimmedSearchTerm = searchTerm.trim();
      if (trimmedSearchTerm.length >= 5) {
        setIsAddressOptionsVisible(true);
        void handleFindAddress(trimmedSearchTerm);
      } else {
        setIsAddressOptionsVisible(false);
        setAddressList([]);
      }
    },
    500,
    [searchTerm],
  );

  return (
    <>
      <div className="flex flex-col md:flex-row items-center gap-x-3">
        <div className="relative w-full">
          <Command
            ref={commandRef}
            className={cn("rounded-lg border border-input", "[&_[cmdk-input-wrapper]]:border-b-0")}
          >
            <CommandInput
              placeholder="Search for address..."
              onValueChange={(value) => {
                setSearchTerm(value);
              }}
              onFocus={() => {
                if (addressList.length > 0) {
                  setIsAddressOptionsVisible(true);
                }
              }}
            />

            {isAddressOptionsVisible && (
              <div className="absolute top-full left-0 right-0 z-50">
                <CommandList>
                  <div className="w-full rounded-md border border-border bg-background shadow-sm">
                    {isLoading ? (
                      <CommandLoading>
                        <Spinner />
                      </CommandLoading>
                    ) : (
                      <>
                        <CommandEmpty>No addresses found</CommandEmpty>
                        <CommandGroup className="md:max-h-[150px] overflow-y-auto">
                          {addressList.map((address) => (
                            <CommandItem
                              key={address.Id}
                              value={`${address.Text} ${address.Description}`}
                              onSelect={() => void handleAddressSelect(address.Id)}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{address.Text}</span>
                                <span className="text-sm text-gray-500 italic">{address.Description}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </>
                    )}
                  </div>
                </CommandList>
              </div>
            )}
          </Command>
        </div>
        <Button
          variant="outline"
          className="w-full md:w-1/3 h-10 mt-2 md:mt-0 border-input"
          onClick={() => setShowAdditionalAddressFields(true)}
        >
          Enter Manually
        </Button>
      </div>
      <div className="min-h-[20px] mt-2">
        {hasError ? (
          <p className="text-[0.8rem] font-medium text-red-500 dark:text-red-900">Address is required.</p>
        ) : null}
      </div>
    </>
  );
}

export function AddressFields({ control }: { control: Control<z.infer<typeof contactSchema>> }) {
  const stateCodes = Object.keys(STATE_DATA);
  const stateOptions = getStateOptions(stateCodes);

  return (
    <>
      <FormField
        control={control}
        name="addressLine1"
        render={({ field }) => (
          <FormItemWithMessage
            label="Address Line 1 *"
            inputComponent={<Input placeholder="Enter address line 1" {...field} />}
          />
        )}
      />
      <FormField
        control={control}
        name="addressLine2"
        render={({ field }) => (
          <FormItemWithMessage
            label="Address Line 2"
            inputComponent={<Input placeholder="Enter address line 2" {...field} />}
          />
        )}
      />
      <FormField
        control={control}
        name="postCode"
        render={({ field }) => (
          <FormItemWithMessage label="ZIP Code *" inputComponent={<Input placeholder="Enter ZIP Code" {...field} />} />
        )}
      />
      <FormField
        control={control}
        name="city"
        render={({ field }) => (
          <FormItemWithMessage label="City *" inputComponent={<Input placeholder="Enter city" {...field} />} />
        )}
      />
      <FormField
        control={control}
        name="state"
        render={({ field }) => (
          <FormItemWithMessage
            label="State *"
            inputComponent={
              <SelectInput {...field} placeholder="Select state" options={stateOptions} value={field.value || ""} />
            }
          />
        )}
      />
    </>
  );
}

export const getRoleOptions = (roles: readonly SupplyChainPartnerRole[]) => {
  return roles.map((role) => ({
    value: role,
    label: formatEnumLabel(role),
  }));
};

type SupplyChainPartnerType = NonNullable<
  NonNullable<ResultOf<typeof supplyChainPartnerTypesQuery>>["supplyChainPartnerTypes"]
>["edges"];

export const getTypeOptions = (edges: SupplyChainPartnerType): ComboboxOption[] => {
  return edges.map((edge) => {
    const node = edge?.node;
    const id = node?.id || "";
    const label = capitaliseFirstLetter(node?.label || "");

    return {
      value: id,
      label: label,
    };
  });
};

export const businessUnitUsersQuery = graphql(`
  query BusinessUnitUsers($businessUnitId: Int) {
    businessUnitUsers(businessUnitId: $businessUnitId) {
      id
      user {
        id
        firstName
        lastName
      }
    }
  }
`);

export const supplyChainPartnerTypesQuery = graphql(`
  query SupplyChainPartnerTypes {
    supplyChainPartnerTypes {
      edges {
        node {
          id
          label
          role
        }
      }
    }
  }
`);
